import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Ticket, ArrowLeft, CheckCircle2, User, Calendar } from 'lucide-react';
import axios from 'axios';
import GoogleSignInButton from '../components/GoogleSignInButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendeeLogin = () => {
  const [step, setStep] = useState(1); // 1: Enter code, 2: Login/Register
  const [isLogin, setIsLogin] = useState(true);
  const [eventCode, setEventCode] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [batchYear, setBatchYear] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!eventCode.trim()) {
      toast.error('Please enter an event code');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-code/${eventCode.toUpperCase()}`);
      setEventInfo(response.data.event);
      setStep(2);
      toast.success('Event found!');
    } catch (error) {
      toast.error('Invalid event code. Please check and try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (isLogin) {
        response = await axios.post(`${API_URL}/api/auth/login/attendee`, {
          email,
          password,
          event_code: eventCode.toUpperCase()
        });
      } else {
        response = await axios.post(`${API_URL}/api/auth/register/attendee`, {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          batch_year: batchYear ? parseInt(batchYear) : null,
          event_code: eventCode.toUpperCase()
        });
      }
      
      const { token, user, event } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('eventInfo', JSON.stringify(event));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuth(user, token, event);
      
      toast.success(isLogin ? 'Welcome back!' : 'Registration successful!');
      navigate('/attendee');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-maroon/90 to-maroon-hover/90" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-8">
            <Ticket className="h-10 w-10" />
          </div>
          <h1 className="font-heading text-5xl font-bold mb-4">Welcome, Attendee!</h1>
          <p className="text-xl text-white/90 max-w-md">
            Join your batchmates and be part of the reunion. Enter your event code to get started.
          </p>
          
          {eventInfo && (
            <div className="mt-8 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <p className="text-sm text-white/60 mb-2">You're joining:</p>
              <h2 className="font-heading text-2xl font-bold text-gold">{eventInfo.school_name}</h2>
              <p className="text-lg text-white/90 mt-1">{eventInfo.event_name}</p>
              {eventInfo.event_date && (
                <p className="text-sm text-white/60 mt-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(eventInfo.event_date).toLocaleDateString('en-PH', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-cream">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-maroon hover:underline mb-6">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          {step === 1 ? (
            /* Step 1: Enter Event Code */
            <Card className="border-0 shadow-xl bg-white" data-testid="event-code-card">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="font-heading text-2xl text-center text-maroon">
                  Enter Event Code
                </CardTitle>
                <CardDescription className="text-center">
                  Ask your organizer for the event code to join
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerifyCode} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Event Code</Label>
                    <div className="relative">
                      <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        value={eventCode}
                        onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                        placeholder="e.g., MSHS2025"
                        className="pl-10 h-14 bg-cream-paper text-center text-xl font-mono tracking-widest uppercase"
                        maxLength={10}
                        data-testid="event-code-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      The code is provided by your reunion organizer
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-maroon hover:bg-maroon-hover text-white rounded-full font-medium text-lg"
                    disabled={verifying}
                    data-testid="verify-code-btn"
                  >
                    {verifying ? 'Verifying...' : 'Continue'}
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <Link to="/organizer/login" className="text-sm text-gray-500 hover:text-maroon">
                    Are you an organizer? Login here →
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Step 2: Login/Register */
            <Card className="border-0 shadow-xl bg-white" data-testid="attendee-auth-card">
              <CardHeader className="space-y-1 pb-4">
                {/* Event Badge */}
                <div className="bg-maroon/10 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-maroon/60">Joining Event</p>
                      <p className="font-heading font-semibold text-maroon">{eventInfo?.school_name}</p>
                      <p className="text-sm text-gray-600">{eventInfo?.event_name}</p>
                    </div>
                    <Badge className="bg-gold text-cream-dark">
                      {eventCode}
                    </Badge>
                  </div>
                  <button
                    onClick={() => { setStep(1); setEventInfo(null); }}
                    className="text-xs text-maroon hover:underline mt-2"
                  >
                    Change event code
                  </button>
                </div>
                
                <CardTitle className="font-heading text-2xl text-center text-maroon">
                  {isLogin ? 'Sign In' : 'Create Account'}
                </CardTitle>
                <CardDescription className="text-center">
                  {isLogin ? 'Welcome back!' : 'Register to join this reunion'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>First Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              placeholder="Maria"
                              required={!isLogin}
                              className="pl-10 bg-cream-paper"
                              data-testid="attendee-firstname"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Last Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              placeholder="Santos"
                              required={!isLogin}
                              className="pl-10 bg-cream-paper"
                              data-testid="attendee-lastname"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Batch Year</Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="number"
                            value={batchYear}
                            onChange={(e) => setBatchYear(e.target.value)}
                            placeholder="1998"
                            min="1950"
                            max="2030"
                            className="pl-10 bg-cream-paper"
                            data-testid="attendee-batchyear"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="maria@example.com"
                        className="pl-10 h-12 bg-cream-paper"
                        required
                        data-testid="attendee-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Password</Label>
                      {isLogin && (
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-xs text-maroon hover:underline"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-12 bg-cream-paper"
                        required
                        data-testid="attendee-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-maroon hover:bg-maroon-hover text-white rounded-full font-medium text-lg"
                    disabled={loading}
                    data-testid="attendee-submit"
                  >
                    {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Join Reunion')}
                  </Button>
                </form>
                
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                  <div className="relative flex justify-center"><span className="px-3 bg-white text-sm text-gray-400">or</span></div>
                </div>
                <GoogleSignInButton portal="attendee" eventCode={eventCode} />

                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    {isLogin ? "New to this event?" : "Already registered?"}{' '}
                    <button
                      onClick={() => setIsLogin(!isLogin)}
                      className="text-maroon font-medium hover:underline"
                      data-testid="toggle-attendee-auth"
                    >
                      {isLogin ? 'Register here' : 'Sign in here'}
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle className="text-maroon">Reset Password</CardTitle>
                  <CardDescription>
                    Enter your email and event code. We'll send you instructions to reset your password.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="maria@example.com"
                      className="bg-cream-paper"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowForgotPassword(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-maroon hover:bg-maroon-hover text-white"
                      onClick={async () => {
                        if (!resetEmail) { toast.error('Please enter your email'); return; }
                        try {
                          await axios.post(`${API_URL}/api/auth/forgot-password`, {
                            email: resetEmail,
                            event_code: eventCode,
                            portal: 'attendee'
                          });
                        } catch (e) { /* silent */ }
                        toast.success('If an account exists, password reset instructions have been sent to your email.');
                        setShowForgotPassword(false);
                        setResetEmail('');
                      }}
                    >
                      Send Reset Link
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendeeLogin;
